import {useEffect, useRef, useState} from "react";
import type {FormEvent} from "react";
import {ArrowLeft} from "lucide-react";
import {toast} from "sonner";
import "react-datepicker/dist/react-datepicker.css";
import {
    login,
    register,
    redirectToYandexOAuth,
    redirectToMessengerOAuth,
    forgotPassword,
    isTwoFactorChallenge,
    verifyLoginCode,
    resendLoginCode,
    vkLogin,
    type TwoFactorChallenge,
} from "../../../services/authService";
import type { VKLoginResult } from "../../../components/auth/VKLoginButton";
import {uploadFile} from "../../../services/uploadService";
import {useAuth} from "../../../contexts/AuthContext";
import {formatRuPhoneInput, isValidRuMobilePhone, validateEmailFormat} from "../../../utils/phone";
import {hapticError} from "../../../utils/haptics";
import {ModalShell, Button, ScrollArea} from "../../ui";
import {PATHS} from "../../../routing";
import {LoginForm} from "./Login";
import {ResetForm} from "./Reset";
import {ResetTokenForm} from "./ResetToken";
import {TwoFactorForm} from "./TwoFactor";
import {PASSWORD_MISMATCH_ERROR, createInitialFormData, getBirthDateError, getInterestsError} from "./constants";
import {getCityChoiceError} from "../../../utils/russianCities";
import type {AuthErrors, AuthFormData, AuthModalProps, AuthMode} from "./types";
import {useNavigate} from "react-router";
import {useSoftKeyboardAssist} from "../../../hooks/useSoftKeyboardAssist";
import {useHistoryLayers} from "../../../hooks/useHistoryLayers";
import {RESET_ERRORS, RESET_SENT_TEXT, forgotPasswordErrorText} from "./resetPassword";

export function AuthModal(
    {
        show = true,
        onClose,
        onAuthSuccess,
        onSuccess,
        initialMode = "login",
        notice,
        resetToken,
        entryMode = false,
    }: AuthModalProps) {
    const {refreshUser} = useAuth();
    const formScrollRef = useRef<HTMLElement | null>(null);
    // Клавиатура на телефоне: Enter/тап мимо поля убирают её, кнопка остаётся достижимой.
    const keyboardAssistRef = useRef<HTMLDivElement | null>(null);
    useSoftKeyboardAssist(keyboardAssistRef);

    const [mode, setMode] = useState<AuthMode>(initialMode);
    const [resetEmail, setResetEmail] = useState("");
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [resetSubmitting, setResetSubmitting] = useState(false);
    const [resetError, setResetError] = useState<string | null>(null);
    /** Когда ушла последняя ссылка — от этого момента идёт таймер «Отправить ещё раз» (#1342). */
    const [resetSentAt, setResetSentAt] = useState<number | null>(null);
    const [resetResending, setResetResending] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState<AuthFormData>(createInitialFormData());
    const [errors, setErrors] = useState<AuthErrors>({});
    // Отказ входа относится к паре «логин + пароль», а не к одному полю. Раньше
    // его клали в errors.email И в errors.password — одна строка, но напечатанная
    // дважды, под обоими полями. Здесь она живёт отдельно и рисуется один раз
    // плашкой над формой (LoginForm.formError).
    const [loginError, setLoginError] = useState<string | null>(null);
    /**
     * Единственная обратная связь при тапе «Войти» — раньше кнопка не менялась
     * никак, пока не приходил ответ сервера. На реальном мобильном соединении
     * через CapacitorHttp это несколько секунд полной тишины, которые читаются
     * как «кнопка не работает» (жалоба Ирины 01.10, 00:03). Паттерн — тот же,
     * что уже есть у resetSubmitting/twoFactorSubmitting в этом компоненте.
     */
    const [loginSubmitting, setLoginSubmitting] = useState(false);
    // Второй фактор: заполнено между «пароль принят» и «код подтверждён».
    const [twoFactor, setTwoFactor] = useState<TwoFactorChallenge | null>(null);
    const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
    // Счётчик неудач: два одинаковых «Неверный код» подряд должны заново
    // тряхнуть поля и очистить их, а по одной лишь строке ошибки это не отличить.
    const [twoFactorErrorKey, setTwoFactorErrorKey] = useState(0);
    const [twoFactorSubmitting, setTwoFactorSubmitting] = useState(false);
    const [twoFactorResending, setTwoFactorResending] = useState(false);

    const navigate = useNavigate();

    // Модалка рендерит только ResetForm либо LoginForm (формы регистрации здесь нет —
    // регистрация живёт на /register). Поэтому всё, что не сброс пароля, обрабатываем
    // как логин: иначе при mode="register" сабмит уходил в валидацию регистрации,
    // требующую отсутствующие в форме поля, и кнопка «Войти» молча ничего не делала.
    // Режим resetToken (новый пароль по ссылке из письма) — отдельная форма
    // ResetTokenForm, поэтому он тоже не логин.
    const isLogin = mode !== "reset" && mode !== "resetToken";
    const isReset = mode === "reset";
    const isResetToken = mode === "resetToken";
    const isTwoFactorStep = isLogin && twoFactor !== null;
    // В режиме входа-точки-входа (Capacitor, `/?auth=login`) стрелка «Назад» на
    // базовом шаге логина ведёт в никуда — closeAuth() закрыл бы модалку на
    // пустое место, ничего за ней не отрисовано. На 2FA и сбросе пароля она
    // остаётся: там это шаг НАЗАД по сценарию, а не закрытие.
    const hideBackArrow = entryMode && !isTwoFactorStep && !isReset && !isResetToken;

    useEffect(() => {
        if (show) {
            setMode(initialMode);
            // Новое открытие модалки — новый вход: недоведённый челлендж не переносим.
            setTwoFactor(null);
            setTwoFactorError(null);
            setLoginError(null);
        }
    }, [show, initialMode]);

    useEffect(() => {
        if (show && formScrollRef.current) {
            formScrollRef.current.scrollTop = 0;
        }
    }, [show, mode, initialMode]);

    const handleSuccess = async () => {
        await refreshUser();
        if (onSuccess) await Promise.resolve(onSuccess());
        if (onAuthSuccess) await Promise.resolve(onAuthSuccess());
    };

    const getEmailOrPhoneError = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) return null;

        if (/[а-яё]/i.test(trimmed)) {
            return "Необходимо использовать латинские буквы";
        }

        if (trimmed.includes("@")) {
            return validateEmailFormat(trimmed).error;
        }

        if (/^\+?\d[\d\s()-]*$/.test(trimmed)) {
            return isValidRuMobilePhone(trimmed) ? null : "Введите корректный номер (российский мобильный)";
        }

        return null;
    };

    const validateField = (field: keyof AuthFormData, value: string | boolean) => {
        if (typeof value !== "string") return null;

        if (field === "email") {
            if (!value.trim()) {
                return isLogin ? "Введите email или телефон" : "Введите email";
            }
            if (isLogin) {
                return getEmailOrPhoneError(value);
            }
            return validateEmailFormat(value).error;
        }

        if (field === "loginPhone") {
            if (!value.trim()) return "Введите номер телефона";
            return isValidRuMobilePhone(value) ? null : "Введите корректный номер (российский мобильный)";
        }

        if (field === "password") {
            if (!value) return "Введите пароль";
            if (!isLogin && value.length < 6) return "Пароль должен содержать минимум 6 символов";
            return null;
        }

        if (field === "confirmPassword") {
            if (!value) return PASSWORD_MISMATCH_ERROR;
            if (value !== formData.password) return PASSWORD_MISMATCH_ERROR;
            return null;
        }

        if (field === "name") {
            return value.trim() ? null : "Введите имя";
        }

        if (field === "birthDate") {
            return getBirthDateError(value);
        }

        if (field === "birthCity" || field === "location") {
            return getCityChoiceError(value);
        }

        if (field === "interests") {
            return getInterestsError(value);
        }

        if (field === "avatarUrl") {
            return value.trim() ? null : "Загрузите фото профиля";
        }

        return null;
    };

    /**
     * Отказ формы: подсветить поля и дать РОВНО ОДИН тактильный отклик на попытку.
     *
     * Эта форма — единственная крупная в приложении, которая сообщает об ошибках
     * только текстом под полями и никогда не зовёт `toast.error`, поэтому общий
     * перехват из `utils/toastHaptics.ts` её не покрывает. Отклик вешаем на факт
     * отказа, а не на каждое поле: пять невалидных полей — это одна неудачная
     * попытка отправки, а не пять событий, и пять вибраций подряд ощущались бы
     * поломкой.
     *
     * На `handleBlur` отклика намеренно нет: человек ещё заполняет форму, он не
     * просил её проверять, и вибрация на каждый уход из поля превратила бы
     * регистрацию в полосу препятствий.
     */
    const rejectWithErrors = (next: AuthErrors) => {
        setErrors(next);
        if (Object.keys(next).length > 0) hapticError();
    };

    const validateForm = () => {
        const newErrors: AuthErrors = {};

        if (isLogin) {
            const raw = formData.email.trim();
            if (!raw) {
                newErrors.email = "Введите email или телефон";
            } else {
                const loginError = getEmailOrPhoneError(raw);
                if (loginError) {
                    newErrors.email = loginError;
                }
            }
        } else if (formData.authMethod === "email") {
            if (!formData.email.trim()) {
                newErrors.email = "Введите email";
            } else {
                const emailError = validateEmailFormat(formData.email).error;
                if (emailError) {
                    newErrors.email = emailError;
                }
            }
        } else if (!formData.loginPhone.trim()) {
            newErrors.loginPhone = "Введите номер телефона";
        } else if (!isValidRuMobilePhone(formData.loginPhone)) {
            newErrors.loginPhone = "Введите корректный номер (российский мобильный)";
        }

        if (!formData.password) {
            newErrors.password = "Введите пароль";
        } else if (!isLogin && formData.password.length < 6) {
            newErrors.password = "Пароль должен содержать минимум 6 символов";
        }

        if (!isLogin && formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = PASSWORD_MISMATCH_ERROR;
        }

        if (!isLogin && !formData.agreeToPrivacy) {
            newErrors.agreeToPrivacy = "Необходимо согласие на обработку данных";
        }
        if (!isLogin && !formData.agreeToTerms) {
            newErrors.agreeToTerms = "Необходимо согласие с пользовательским соглашением";
        }
        if (!isLogin && !formData.agreeToOffer) {
            newErrors.agreeToOffer = "Необходимо согласие с публичной офертой";
        }
        if (!isLogin && !formData.agreeToAge18) {
            newErrors.agreeToAge18 = "Подтвердите, что вам 18 лет или больше";
        }

        if (!isLogin) {
            if (!formData.name.trim()) newErrors.name = "Введите имя";
            const birthDateError = getBirthDateError(formData.birthDate);
            if (birthDateError) newErrors.birthDate = birthDateError;
            if (!formData.avatarUrl) newErrors.avatarUrl = "Загрузите фото профиля";
            const birthCityError = getCityChoiceError(formData.birthCity);
            if (birthCityError) newErrors.birthCity = birthCityError;
            const locationError = getCityChoiceError(formData.location);
            if (locationError) newErrors.location = locationError;
            const interestsError = getInterestsError(formData.interests);
            if (interestsError) newErrors.interests = interestsError;
        }

        rejectWithErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleBlur = (field: keyof AuthFormData, value: string | boolean) => {
        if (typeof value === "string") {
            setFormData((prev) => ({...prev, [field]: value}));
        }

        // Человек правит логин или пароль — прошлый отказ сервера уже про другую пару.
        setLoginError(null);

        const error = validateField(field, value);
        if (!error) {
            setErrors((prev) => {
                const newErrors = {...prev};
                delete newErrors[field];
                return newErrors;
            });
            return;
        }

        setErrors((prev) => ({...prev, [field]: error}));
    };

    const handleAuthMethodChange = (method: "email" | "phone") => {
        setFormData((prev) => ({...prev, authMethod: method}));
        setErrors((prev) => {
            const next = {...prev};
            delete next.email;
            delete next.loginPhone;
            return next;
        });
    };

    const handleToggleAuthMode = () => {
        onClose?.();
        navigate(PATHS.register);
    };

    /** Шаг «Восстановление пароля» → обратно ко входу (кнопки «Назад» и системная «назад»). */
    const leaveReset = () => {
        setMode("login");
        setResetEmail("");
        setResetEmailSent(false);
        setResetError(null);
        setResetSentAt(null);
    };

    // Системная «назад» (браузер, Android в Capacitor) на шаге сброса возвращает ко
    // входу, а не уводит со страницы/из приложения (#1342): шаг кладёт свою запись
    // в историю, как слои-страницы разборов. Адрес не меняется.
    const resetLayer = useHistoryLayers({
        ns: "authReset",
        count: isReset ? 1 : 0,
        onTrim: leaveReset,
    });

    const handleResetPassword = async (e: FormEvent) => {
        e.preventDefault();
        const email = resetEmail.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setResetError(RESET_ERRORS.email);
            // Локальная проверка — отвечаем сами. Ниже отказ сервера уже идёт
            // через toast.error, и там отклик даёт общий перехват: если добавить
            // хаптик и здесь, и там, на серверной ошибке он сработает дважды.
            hapticError();
            return;
        }
        setResetError(null);
        setResetSubmitting(true);
        const res = await forgotPassword(email);
        setResetSubmitting(false);
        if (res.error) {
            // 422 сервера приходит сырым JSON pydantic — показываем русский текст.
            const message = forgotPasswordErrorText(res.error, res.code);
            setResetError(message);
            toast.error(message);
            return;
        }
        setResetEmailSent(true);
        setResetSentAt(Date.now());
        toast.success(RESET_SENT_TEXT.toast);
    };

    const handleResendReset = async () => {
        setResetResending(true);
        const res = await forgotPassword(resetEmail.trim());
        setResetResending(false);
        if (res.error) {
            toast.error(forgotPasswordErrorText(res.error, res.code));
            return;
        }
        setResetSentAt(Date.now());
        toast.success(RESET_SENT_TEXT.resent);
    };

    /** VK ID login: получает данные от SDK через VKLoginButton → обмен на JWT. */
    const handleLoginWithVK = async (vkData: VKLoginResult) => {
        setLoginError(null);
        const res = await vkLogin(vkData);
        if (res.error) {
            setLoginError(res.error);
            toast.error(res.error);
            return;
        }
        await handleSuccess();
    };

    /**
     * Ошибки VK разделены по фазе. «init» — виджет не поднялся (SDK не отдался,
     * домен не заведён в VK ID, таймаут): пользователь ничего не нажимал, и
     * красная плашка поверх формы входа по email его только пугала. Такие
     * ошибки уходят в консоль, кнопка VK просто не появляется. «login» — сбой
     * уже после нажатия, вот о нём и сообщаем.
     */
    const handleVKError = (error: any, phase: "init" | "login" = "login") => {
        console.error(`VK login error (${phase}):`, error);
        if (phase === "init") return;
        setLoginError("Ошибка входа через VK. Попробуйте позже.");
    };

    const failTwoFactor = (message: string) => {
        setTwoFactorError(message);
        setTwoFactorErrorKey((prev) => prev + 1);
        // Код вводят по одной цифре, глядя на клавиатуру, а не на поле: отклик
        // здесь часто приходит раньше, чем человек поднимет глаза на текст ошибки.
        hapticError();
    };

    /** Шаг 2 входа: код из письма в обмен на сессию. */
    const handleVerifyCode = async (code: string) => {
        if (!twoFactor || twoFactorSubmitting) return;
        setTwoFactorSubmitting(true);
        setTwoFactorError(null);
        const res = await verifyLoginCode(twoFactor.challengeId, code);
        setTwoFactorSubmitting(false);
        if (res.error || !res.data) {
            failTwoFactor(res.error || "Не удалось подтвердить код");
            return;
        }
        await handleSuccess();
    };

    /** Повторная отправка: сервер выдаёт новый challengeId, старый код умирает. */
    const handleResendCode = async () => {
        if (!twoFactor || twoFactorResending) return;
        setTwoFactorResending(true);
        setTwoFactorError(null);
        const res = await resendLoginCode(twoFactor.challengeId);
        setTwoFactorResending(false);
        if (res.error || !res.data) {
            failTwoFactor(res.error || "Не удалось отправить код");
            return;
        }
        setTwoFactor(res.data);
        toast.success("Новый код отправлен на почту");
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoginError(null);

        if (isLogin) {
            // Игнорируем повторный тап, пока первый запрос ещё в полёте — без
            // этого нетерпеливый повторный тап на медленной сети уходил бы
            // вторым параллельным login() и мог привести к гонке ответов.
            if (loginSubmitting) return;

            // Поля логина неуправляемые (defaultValue + onBlur): автозаполнение
            // менеджером паролей не вызывает blur, и стейт остаётся пустым.
            // Берём фактические значения из DOM-формы.
            let loginEmail = formData.email;
            let loginPassword = formData.password;
            if (e.currentTarget instanceof HTMLFormElement) {
                const fd = new FormData(e.currentTarget);
                const domEmail = String(fd.get("username") ?? "").trim();
                const domPassword = String(fd.get("password") ?? "");
                if (domEmail) loginEmail = domEmail;
                if (domPassword) loginPassword = domPassword;
                if (loginEmail !== formData.email || loginPassword !== formData.password) {
                    setFormData((p) => ({...p, email: loginEmail, password: loginPassword}));
                }
            }

            const newErrors: AuthErrors = {};
            const rawEmail = loginEmail.trim();
            if (!rawEmail) {
                newErrors.email = "Введите email или телефон";
            } else {
                const loginError = getEmailOrPhoneError(rawEmail);
                if (loginError) newErrors.email = loginError;
            }
            if (!loginPassword) newErrors.password = "Введите пароль";
            rejectWithErrors(newErrors);
            if (Object.keys(newErrors).length > 0) return;

            setLoginSubmitting(true);
            try {
                const response = await login({
                    email: loginEmail,
                    password: loginPassword,
                });

                if (response.error) {
                    // Один текст — одна плашка над формой; поля получают только кольцо.
                    setLoginError(response.error);
                    setErrors({});
                    // Отказ сервера — тот же класс события, что и невалидная форма:
                    // отправили и не пустили. Плашка появляется над формой, куда
                    // человек в этот момент может и не смотреть.
                    hapticError();
                } else if (response.data) {
                    if (isTwoFactorChallenge(response.data)) {
                        // Пароль верный, но сессии ещё нет — показываем экран ввода кода.
                        setTwoFactor(response.data);
                        setTwoFactorError(null);
                        return;
                    }
                    await handleSuccess();
                }
            } finally {
                setLoginSubmitting(false);
            }
            return;
        }

        if (!validateForm()) return;

        try {
            {
                const response = await register({
                    authMethod: formData.authMethod,
                    email: formData.authMethod === "email" ? formData.email.trim() : undefined,
                    loginPhone: formData.authMethod === "phone" ? formData.loginPhone.trim() : undefined,
                    password: formData.password,
                    agreeToPrivacy: formData.agreeToPrivacy,
                    agreeToTerms: formData.agreeToTerms,
                    agreeToOffer: formData.agreeToOffer,
                    agreeToAge18: formData.agreeToAge18,
                    name: formData.name.trim(),
                    birthDate: formData.birthDate,
                    birthTime: formData.birthTime.trim() || undefined,
                    birthCity: formData.birthCity.trim() || undefined,
                    gender: formData.gender || "female",
                    avatarUrl: formData.avatarUrl,
                    bio: formData.bio.trim() || undefined,
                    location: formData.location.trim() || undefined,
                    interests: (() => {
                        const list = formData.interests
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean);
                        return list.length ? list : undefined;
                    })(),
                });

                if (response.error) {
                    rejectWithErrors(
                        formData.authMethod === "email"
                            ? {email: response.error}
                            : {loginPhone: response.error}
                    );
                } else if (response.data) {
                    // Инструкция при первом заходе после регистрации — один раз (d5ac7a26).
                    try {
                        localStorage.setItem("rl_show_onboarding_guide", "1");
                    } catch {
                        /* noop */
                    }
                    await handleSuccess();
                }
            }
        } catch (error) {
            console.error("Auth error:", error);
            rejectWithErrors({password: "Произошла ошибка. Попробуйте еще раз."});
        }
    };

    return (
        <ModalShell
            onClose={onClose}
            ariaLabel={isReset ? "Восстановление пароля" : isResetToken ? "Новый пароль" : isTwoFactorStep ? "Подтверждение входа" : "Вход"}
            disableBackdropClose
            hideCloseButton
            fitContent={true}
            maxHeight="100%"
            fullPage={entryMode}
            className="bg-[var(--rla-bg)]"
        >
            <div ref={keyboardAssistRef} className="flex flex-1 min-h-0 flex-col bg-[var(--rla-bg)]">
                <div className="relative flex-shrink-0 px-5 pt-5">
                    {/* Кнопка «Назад» — круглая белая с чёрной стрелкой. Скрыта на базовом
                      * шаге логина в entryMode (см. hideBackArrow) — там ей некуда вести. */}
                    {!hideBackArrow && (
                        <Button
                            type="button"
                            onClick={() => {
                                if (isTwoFactorStep) {
                                    setTwoFactor(null);
                                    setTwoFactorError(null);
                                    return;
                                }
                                if (isReset) {
                                    resetLayer.back(leaveReset);
                                    return;
                                }
                                if (isResetToken) {
                                    setMode("login");
                                    return;
                                }
                                onClose?.();
                            }}
                            variant="ghost"
                            size="iconSm"
                            aria-label="Назад"
                            className="absolute left-5 top-5 size-10 rounded-full bg-white text-gray-800 shadow-sm ring-1 ring-black/[0.06]"
                        >
                            <ArrowLeft className="size-5"/>
                        </Button>
                    )}
                    {/*
                      * Крестика справа нет намеренно: слева уже есть «Назад», который
                      * на логине/регистрации закрывает модалку, а на 2FA/сбросе пароля
                      * возвращает на шаг назад. Два действия закрытия в одном углу
                      * путали — оставлен ровно один вход в навигацию.
                      */}

                    <div className="text-center pt-2 pb-1">
                        {/* Логотип: круг с белым фоном и градиентной обводкой */}
                        <div className="mx-auto mb-4 flex size-24 items-center justify-center rounded-full bg-white shadow-md"
                             style={{
                                 padding: "3px",
                                 background: "linear-gradient(white, white) padding-box, var(--brand-gradient) border-box",
                                 border: "1.5px solid transparent",
                                 borderRadius: "9999px",
                             }}
                        >
                            <img
                                src="/Brand/Matreshka.png"
                                alt="Матрёшка"
                                className="size-20 object-contain"
                            />
                        </div>
                        <h2 className="text-[26px] sm:text-[28px] font-extrabold leading-tight text-gray-900 mb-1.5">
                            {isReset
                                ? "Восстановление пароля"
                                : isResetToken
                                    ? "Новый пароль"
                                    : isTwoFactorStep
                                        ? "Подтверждение входа"
                                        : "С возвращением"}
                        </h2>
                        <p className="text-sm font-medium text-gray-500 max-w-[260px] mx-auto">
                            {isReset
                                ? "Введите email для получения ссылки"
                                : isResetToken
                                    ? "По ссылке из письма"
                                    : isTwoFactorStep
                                        ? "Остался один шаг — код с вашей почты"
                                        : "Войдите, чтобы вернуться к\u00A0своим\u00A0совпадениям"}
                        </p>
                    </div>
                </div>

                <ScrollArea viewportRef={formScrollRef} className="flex-1 min-h-0"><div className="px-5 sm:px-6 py-5" style={{paddingBottom: "calc(1.25rem + var(--rla-kb-inset, 0px))"}}>
                    {notice && (
                        <div
                            role="status"
                            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900"
                        >
                            {notice}
                        </div>
                    )}
                    {isReset ? (
                        <ResetForm
                            resetEmail={resetEmail}
                            resetEmailSent={resetEmailSent}
                            resetSubmitting={resetSubmitting}
                            resetError={resetError}
                            resetSentAt={resetSentAt}
                            resetResending={resetResending}
                            onResetEmailChange={(value) => {
                                setResetEmail(value);
                                setResetError(null);
                            }}
                            onSubmit={handleResetPassword}
                            onResend={() => void handleResendReset()}
                            onBackToLogin={() => resetLayer.back(leaveReset)}
                        />
                    ) : isResetToken ? (
                        <ResetTokenForm token={resetToken ?? ""}/>
                    ) : isTwoFactorStep && twoFactor ? (
                        <TwoFactorForm
                            challengeId={twoFactor.challengeId}
                            emailHint={twoFactor.emailHint}
                            resendAfterSeconds={twoFactor.resendAfterSeconds}
                            error={twoFactorError}
                            errorKey={twoFactorErrorKey}
                            submitting={twoFactorSubmitting}
                            resending={twoFactorResending}
                            onSubmit={handleVerifyCode}
                            onResend={handleResendCode}
                            onClearError={() => setTwoFactorError(null)}
                            onBackToLogin={() => {
                                setTwoFactor(null);
                                setTwoFactorError(null);
                            }}
                        />
                    ) : (
                        <LoginForm
                            email={formData.email}
                            password={formData.password}
                            errors={errors}
                            formError={loginError}
                            submitting={loginSubmitting}
                            showPassword={showPassword}
                            onBlur={handleBlur}
                            onTogglePassword={() => setShowPassword(!showPassword)}
                            onSubmit={handleSubmit}
                            onToggleAuthMode={handleToggleAuthMode}
                            onOpenReset={() => {
                                // Адрес из формы входа переносим в форму сброса (#1342).
                                const loginEmail = formData.email.trim();
                                setResetEmail(loginEmail.includes("@") ? loginEmail : "");
                                setMode("reset");
                                setLoginError(null);
                            }}
                            onLoginWithYandex={() => redirectToYandexOAuth()}
                            onLoginWithMessenger={redirectToMessengerOAuth}
                            onLoginWithVK={handleLoginWithVK}
                            onVKError={handleVKError}
                        />
                    )}
                </div></ScrollArea>
            </div>
        </ModalShell>
    );
}
